import React from 'react';

import PercentageChart from '../reusable/PercentageChart';

// utility to render time in millis to something more human readable
// FIXME: this entire thing needs to be adjusted per language
const renderTimeFromMs = (timeInMs) => {
  let time = timeInMs;

  if (time === 0) {
    return '0s';
  }

  if (time < 1000) {
    return `${time}ms`;
  }

  // in seconds
  time /= 1000;

  if (time < 90) {
    return `${time.toFixed()}s`;
  }

  // in minutes
  time /= 60;

  if (time < 90) {
    return `${time.toFixed()}m`;
  }

  // in hours
  time /= 60;

  if (time < 36) {
    return `${time.toFixed()}h`;
  }

  // in days
  time /= 24;

  return `${time.toFixed()}d`;
};

// utility to render percentage and time in a simple and consistent way
const renderPercentageAndTime = (value, total) => (
  <span>
    {total !== 0 ? (value / total * 100).toFixed(1) : 0}%<br />
    ({renderTimeFromMs(value)})
  </span>
);

// -------------------------------------------------------------------------------------------------------------------

const renderSingleColor = (value, { green, yellow, red }) => renderPercentageAndTime(value, green + yellow + red);
const renderOperatingTime = (v, { green, yellow, red }) => renderPercentageAndTime(green + yellow, green + yellow + red);

// -------------------------------------------------------------------------------------------------------------------

const renderOnTime = (v, {
  green, yellow, red, off,
}) => renderPercentageAndTime(green + yellow + red, green + yellow + red + off);

const renderOnTimeGraph = (v, {
  green, yellow, red, off, id,
}) => {
  const gyr = green + yellow + red;
  const total = gyr + off;

  let values;

  if (total === 0) {
    values = [
      {
        id: 'off',
        start: 0,
        percentage: 100,
        color: '#dedaf7',
        hoverParams: {
          'charts.percentage': 100,
          'charts.light': 'Off',
        },
      },
    ];
  } else {
    const percentageOn = total !== 0 ? (gyr / total * 100) : 0;
    const percentageOff = 100 - percentageOn;

    values = [
      {
        id: 'on',
        start: 0,
        percentage: percentageOn,
        color: '#527AE2',
        hoverParams: {
          'charts.percentage': percentageOn.toFixed(1),
          'common.duration': renderTimeFromMs(gyr),
          'charts.light': 'On',
        },
      },
      {
        id: 'off',
        start: percentageOn,
        percentage: percentageOff,
        color: '#dedaf7',
        hoverParams: {
          'charts.percentage': percentageOff.toFixed(1),
          'common.duration': renderTimeFromMs(total - gyr),
          'charts.light': 'Off',
        },
      },
    ];
  }

  return (
    <PercentageChart componentId={`${id}-ontime`} values={values} />
  );
};

// -------------------------------------------------------------------------------------------------------------------

const renderUtilization = (v, { green, yellow, red }) => renderPercentageAndTime(green, green + yellow + red);

const renderUtilizationGraph = (v, {
  green, yellow, red, id,
}) => {
  const gyr = green + yellow + red;

  let values;
  if (gyr === 0) {
    values = [
      {
        id: 'off',
        start: 0,
        percentage: 100,
        color: '#dedaf7',
        hoverParams: {
          'charts.percentage': 100,
          'charts.light': 'Off',
        },
      },
    ];
  } else {
    const percentageGreen = green / gyr * 100;
    const percentageYellow = yellow / gyr * 100;
    const percentageRed = red / gyr * 100;

    values = [
      {
        id: 'green',
        start: 0,
        percentage: percentageGreen,
        color: '#508642',
        hoverParams: {
          'charts.percentage': percentageGreen.toFixed(1),
          'common.duration': renderTimeFromMs(green),
          'charts.light': 'Green',
        },
      },
      {
        id: 'yellow',
        start: percentageGreen,
        percentage: percentageYellow,
        color: '#e0752d',
        hoverParams: {
          'charts.percentage': percentageYellow.toFixed(1),
          'common.duration': renderTimeFromMs(yellow),
          'charts.light': 'Yellow',
        },
      },
      {
        id: 'red',
        start: percentageGreen + percentageYellow,
        percentage: percentageRed,
        color: '#e24d42',
        hoverParams: {
          'charts.percentage': percentageRed.toFixed(1),
          'common.duration': renderTimeFromMs(red),
          'charts.light': 'Red',
        },
      },
    ];
  }

  return (
    <PercentageChart componentId={`${id}-util`} values={values} />
  );
};

// -------------------------------------------------------------------------------------------------------------------

const metricsShape = new Map();

metricsShape.set('machine', {
  header: {
    title: 'common.machine',
  },
  cell: {
    collapsing: true,
    renderFn: value => <span style={{ fontWeight: 'bold' }}>{value}</span>,
  },
});

metricsShape.set('onTimeGraph', {
  header: {
    title: 'metrics.on-time',
  },
  cell: {
    renderFn: renderOnTimeGraph,
  },
});

metricsShape.set('onTime', {
  header: {
    title: '',
    style: {
      borderLeft: 'none',
    },
  },
  cell: {
    collapsing: true,
    style: {
      borderLeft: 'none',
    },
    renderFn: renderOnTime,
  },
});

metricsShape.set('utilizationGraph', {
  header: {
    title: 'metrics.utilization',
  },
  cell: {
    renderFn: renderUtilizationGraph,
  },
});

metricsShape.set('utilization', {
  header: {
    title: '',
    style: {
      borderLeft: 'none',
    },
  },
  cell: {
    collapsing: true,
    style: {
      borderLeft: 'none',
    },
    renderFn: renderUtilization,
  },
});

metricsShape.set('green', {
  header: {
    title: 'common.green',
  },
  cell: {
    collapsing: true,
    renderFn: renderSingleColor,
  },
});

metricsShape.set('yellow', {
  header: {
    title: 'common.yellow',
  },
  cell: {
    collapsing: true,
    renderFn: renderSingleColor,
  },
});

metricsShape.set('red', {
  header: {
    title: 'common.red',
  },
  cell: {
    collapsing: true,
    renderFn: renderSingleColor,
  },
});

metricsShape.set('operatingTime', {
  header: {
    title: 'metrics.operating-time',
  },
  cell: {
    collapsing: true,
    renderFn: renderOperatingTime,
  },
});

export default metricsShape;



// WEBPACK FOOTER //
// ./src/components/Metrics/MetricsTableShape.js